import { useState, useCallback, useMemo, useEffect } from "react";

/**
 * Utility functions for date handling
 */
export const formatDateForInput = (date) => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const parseDateFromInput = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? null : date;
};

export const isValidDate = (date) => {
  return date instanceof Date && !isNaN(date.getTime());
};

export const isAfterDate = (date1, date2) => {
  if (!date1 || !date2) return false;
  return date1.getTime() > date2.getTime();
};

export const isBeforeDate = (date1, date2) => {
  if (!date1 || !date2) return false;
  return date1.getTime() < date2.getTime();
};

export const formatDisplayDate = (date, format = "dd/MM/yyyy") => {
  if (!date) return "";

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  switch (format) {
    case "dd/MM/yyyy":
      return `${day}/${month}/${year}`;
    case "MM/dd/yyyy":
      return `${month}/${day}/${year}`;
    case "yyyy-MM-dd":
      return `${year}-${month}-${day}`;
    default:
      return `${day}/${month}/${year}`;
  }
};

/**
 * Custom hook for DateRangePicker logic
 */
export const useDateRangePicker = ({
  fromDate,
  toDate,
  onChange,
  minDate = null,
  maxDate = null,
  dateFormat = "dd/MM/yyyy",
  allowFutureDates,
}) => {
  const [errors, setErrors] = useState({
    fromDate: "",
    toDate: "",
    range: "",
  });

  // Memoize today's date to prevent infinite re-renders
  const today = useMemo(() => {
    const date = new Date();
    date.setHours(23, 59, 59, 999);
    return date;
  }, []);

  // Use memoized maxDate to prevent infinite re-renders
  const effectiveMaxDate = useMemo(() => {
    return maxDate || today;
  }, [maxDate, today]);

  // Format dates for HTML date inputs
  const formattedFromDate = formatDateForInput(fromDate);
  const formattedToDate = formatDateForInput(toDate);
  const formattedMinDate = formatDateForInput(minDate);
  const formattedMaxDate = formatDateForInput(effectiveMaxDate);

  // Memoized validation function
  const validateDates = useCallback(
    (from, to) => {
      const newErrors = {
        fromDate: "",
        toDate: "",
        range: "",
      };

      const maxDateToUse = effectiveMaxDate;

      // Validate from date
      if (from) {
        if (!isValidDate(from)) {
          newErrors.fromDate = "Invalid from date";
        } else if (isAfterDate(from, today) && !allowFutureDates) {
          newErrors.fromDate = "From date cannot be in the future";
        } else if (minDate && isBeforeDate(from, minDate)) {
          newErrors.fromDate = `From date cannot be before ${formatDisplayDate(
            minDate,
            dateFormat,
          )}`;
        } else if (isAfterDate(from, maxDateToUse) && !allowFutureDates) {
          newErrors.fromDate = `From date cannot be after ${formatDisplayDate(
            maxDateToUse,
            dateFormat,
          )}`;
        }
      }

      // Validate to date
      if (to) {
        if (!isValidDate(to)) {
          newErrors.toDate = "Invalid to date";
        } else if (isAfterDate(to, today) && !allowFutureDates) {
          newErrors.toDate = "To date cannot be in the future";
        } else if (minDate && isBeforeDate(to, minDate)) {
          newErrors.toDate = `To date cannot be before ${formatDisplayDate(
            minDate,
            dateFormat,
          )}`;
        } else if (isAfterDate(to, maxDateToUse) && !allowFutureDates) {
          newErrors.toDate = `To date cannot be after ${formatDisplayDate(
            maxDateToUse,
            dateFormat,
          )}`;
        }
      }

      // Validate range
      if (from && to && isValidDate(from) && isValidDate(to)) {
        if (isAfterDate(from, to)) {
          newErrors.range = "From date must be before or equal to to date";
        }
      }

      return newErrors;
    },
    [minDate, effectiveMaxDate, dateFormat, today, allowFutureDates],
  );

  // Update errors when dates change
  useEffect(() => {
    const newErrors = validateDates(fromDate, toDate);
    setErrors(newErrors);
  }, [fromDate, toDate, validateDates]);

  // Handle from date change
  const handleFromDateChange = useCallback(
    (event) => {
      const newDate = parseDateFromInput(event.target.value);
      const validatedErrors = validateDates(newDate, toDate);
      setErrors(validatedErrors);

      // Always call onChange to maintain controlled component behavior
      onChange(newDate, toDate);
    },
    [toDate, onChange, validateDates],
  );

  // Handle to date change
  const handleToDateChange = useCallback(
    (event) => {
      const newDate = parseDateFromInput(event.target.value);
      const validatedErrors = validateDates(fromDate, newDate);
      setErrors(validatedErrors);

      // Always call onChange to maintain controlled component behavior
      onChange(fromDate, newDate);
    },
    [fromDate, onChange, validateDates],
  );

  // Get error message to display
  const errorMessage = useMemo(() => {
    return errors.range || errors.fromDate || errors.toDate;
  }, [errors]);

  return {
    errors,
    errorMessage,
    formattedFromDate,
    formattedToDate,
    formattedMinDate,
    formattedMaxDate,
    handleFromDateChange,
    handleToDateChange,
  };
};
